import React, { useState } from 'react';
import { DraftJob } from '@/types/draftJob';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';

interface DraftJobCardProps {
  draftJob: DraftJob;
  onCopy: (draftJob: DraftJob) => void;
  onDelete: (draftJobId: string) => void;
  clients: Array<{
    id: string;
    name: string;
    address?: { suburb?: string; state?: string };
  }>;
}

const DraftJobCard: React.FC<DraftJobCardProps> = ({
  draftJob,
  onCopy,
  onDelete,
  clients,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const selectedClient = clients.find(
    (client) => client.id === draftJob.clientId,
  );
  const siteLabel =
    draftJob.siteName ||
    (selectedClient?.address
      ? `${selectedClient.address.suburb || ''}, ${selectedClient.address.state || ''}`.trim()
      : '');

  const formatDate = (dateStr: string) => {
    return dayjs(dateStr).format('ddd, D MMM');
  };

  const formatTime = (startTime: string, endTime: string) => {
    const start = dayjs(`2000-01-01T${startTime}`).format('h:mm A');
    const end = dayjs(`2000-01-01T${endTime}`).format('h:mm A');
    return `${start} - ${end}`;
  };

  const workerText = draftJob.workersNeeded === 1 ? 'worker' : 'workers';

  return (
    <div className="mb-4 rounded-lg border-l-4 border-l-purple-500 bg-gray-50 p-4 shadow-sm">
      {/* Company Information Section */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {/* Company Logo Placeholder */}
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-300">
            <svg
              className="h-6 w-6 text-gray-600"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>

          <div className="flex-1">
            <div className="font-semibold text-gray-800">
              {selectedClient?.name || 'Unknown Client'}
            </div>
            {siteLabel ? (
              <div className="text-sm text-gray-600">{siteLabel}</div>
            ) : null}
          </div>
        </div>

        {/* Dropdown Chevron */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-500 hover:text-gray-700"
        >
          <i
            className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-sm`}
          ></i>
        </button>
      </div>

      {/* Job Title */}
      <div className="mb-3">
        <h3 className="text-lg font-bold text-gray-800">
          {draftJob.classification?.name || 'Unknown Role'}
        </h3>
      </div>

      {/* Expandable Job Details */}
      {isExpanded && (
        <>
          {/* Separator Line */}
          <div className="mb-3 border-t border-gray-300"></div>

          {/* Job Details Row */}
          <div className="mb-4 flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>{formatDate(draftJob.startDate)}</span>
            </div>

            <div className="h-1 w-1 rounded-full bg-gray-400"></div>

            <div className="flex items-center space-x-1">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{formatTime(draftJob.startTime, draftJob.endTime)}</span>
            </div>

            <div className="h-1 w-1 rounded-full bg-gray-400"></div>

            <div className="flex items-center space-x-1">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span>
                {draftJob.workersNeeded} {workerText}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-3">
        {isExpanded ? (
          <>
            <button
              onClick={() => onCopy(draftJob)}
              className="rounded p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              title="Copy job"
            >
              <FontAwesomeIcon icon={faCopy} className="h-4 w-4" />
            </button>

            <button
              onClick={() => onDelete(draftJob.id)}
              className="rounded p-2 text-gray-500 hover:bg-gray-200 hover:text-red-600"
              title="Delete job"
            >
              <FontAwesomeIcon icon={faTrashCan} className="h-4 w-4" />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default DraftJobCard;
